import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Calculator, Brain, History, PiggyBank,
  Wallet, Zap, CheckCircle, XCircle, ChevronRight, Star, Gift, Trophy, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";

const MASCOT = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

const TOOLS = [
  { id: "lessons", label: "Lessons", icon: BookOpen, color: "text-blue-400", desc: "21 kid-friendly Bitcoin sections" },
  { id: "sat-math", label: "Bitcoin Math", icon: Calculator, color: "text-[#F7931A]", desc: "Math with Bitcoin and BTC XP" },
  { id: "vocab", label: "Vocabulary", icon: Brain, color: "text-purple-400", desc: "Bitcoin terms flash cards" },
  { id: "history", label: "History Quiz", icon: History, color: "text-green-400", desc: "Bitcoin history questions" },
  { id: "allowance", label: "Sat Budget", icon: PiggyBank, color: "text-pink-400", desc: "Editable sat budget simulator" },
  { id: "budget", label: "Budget Challenge", icon: Wallet, color: "text-cyan-400", desc: "Plan a family budget" },
];

export default function KidMode() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tool, setTool] = useState("menu");
  const [loading, setLoading] = useState(false);

  // Sat Math state
  const [mathProblems, setMathProblems] = useState([]);
  const [mathIndex, setMathIndex] = useState(0);
  const [mathAnswer, setMathAnswer] = useState("");
  const [mathResult, setMathResult] = useState(null);

  // Vocab state
  const [vocabTerms, setVocabTerms] = useState([]);
  const [vocabIndex, setVocabIndex] = useState(0);
  const [showDef, setShowDef] = useState(false);
  const [vocabGuess, setVocabGuess] = useState("");

  // History Quiz state
  const [historyQs, setHistoryQs] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [historyAnswer, setHistoryAnswer] = useState(null);
  const [historyPhase, setHistoryPhase] = useState("idle");
  const [historyScore, setHistoryScore] = useState(0);
  const [historyDone, setHistoryDone] = useState(false);

  // Allowance state
  const [allowance, setAllowance] = useState(null);
  const [allocAmount, setAllocAmount] = useState("50");
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");

  // Budget state
  const [budget, setBudget] = useState(null);
  const [budgetAlloc, setBudgetAlloc] = useState({});
  const [budgetResult, setBudgetResult] = useState(null);

  // Kid sections
  const [kidSections, setKidSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionDetail, setSectionDetail] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [kidQuizAnswers, setKidQuizAnswers] = useState({});
  const [kidQuizResult, setKidQuizResult] = useState(null);
  const [kidQuizSubmitting, setKidQuizSubmitting] = useState(false);

  const loadKidSection = async (sectionId) => {
    setSectionLoading(true);
    setSectionDetail(null);
    setKidQuizAnswers({});
    setKidQuizResult(null);
    try {
      const res = await api.get(`/kid-mode/sections/${sectionId}`);
      setSectionDetail(res.data);
      setSelectedSection(sectionId);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Upgrade to access this section");
        navigate("/subscription");
      } else {
        toast.error("Failed to load section");
      }
    }
    setSectionLoading(false);
  };

  const submitKidQuiz = async () => {
    if (!sectionDetail) return;
    const answers = sectionDetail.quiz.map((q, i) => ({
      question_index: i,
      answer: kidQuizAnswers[i] || ""
    }));
    setKidQuizSubmitting(true);
    try {
      const res = await api.post(`/kid-mode/sections/${sectionDetail.id}/complete`, { answers });
      setKidQuizResult(res.data);
      if (res.data.passed) {
        toast.success(`Passed! +${res.data.xp_earned} BTC XP`);
      } else {
        toast.error("Not quite — try again!");
      }
    } catch (err) {
      toast.error("Failed to submit quiz");
    }
    setKidQuizSubmitting(false);
  };

  const backToSectionList = () => {
    setSectionDetail(null);
    setSelectedSection(null);
    setKidQuizAnswers({});
    setKidQuizResult(null);
    loadTool("lessons");
  };

  const loadTool = async (toolId) => {
    setTool(toolId);
    setLoading(true);
    try {
      if (toolId === "sat-math") {
        const res = await api.get("/kids-tools/sat-math");
        setMathProblems(res.data.problems);
        setMathIndex(0); setMathAnswer(""); setMathResult(null);
      } else if (toolId === "vocab") {
        const res = await api.get("/kids-tools/vocabulary");
        setVocabTerms(res.data.terms);
        setVocabIndex(0); setShowDef(false); setVocabGuess("");
      } else if (toolId === "history") {
        const res = await api.get("/kids-tools/history-quiz");
        setHistoryQs(res.data.questions);
        setHistoryIdx(0); setHistoryAnswer(null); setHistoryPhase("idle");
        setHistoryScore(0); setHistoryDone(false);
      } else if (toolId === "allowance") {
        const res = await api.get("/kids-tools/allowance");
        setAllowance(res.data);
      } else if (toolId === "budget") {
        const res = await api.get("/kids-tools/budget-challenge");
        setBudget(res.data);
        const init = {};
        res.data.categories.forEach(c => { init[c.id] = c.suggested_pct; });
        setBudgetAlloc(init);
        setBudgetResult(null);
      } else if (toolId === "lessons") {
        const res = await api.get("/kid-mode/sections");
        setKidSections(res.data.sections || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── Sat Math Handlers ──
  const checkMath = async () => {
    if (!mathAnswer) return;
    try {
      const res = await api.post("/kids-tools/sat-math/check", {
        problem_id: mathProblems[mathIndex].id, answer: parseFloat(mathAnswer)
      });
      setMathResult(res.data);
      if (res.data.correct) toast.success(`+${res.data.xp_earned} BTC XP!`);
    } catch { toast.error("Error checking answer"); }
  };
  const nextMath = () => {
    setMathIndex(i => Math.min(i + 1, mathProblems.length - 1));
    setMathAnswer(""); setMathResult(null);
  };

  // ── History Quiz Handlers ──
  const handleHistoryAnswer = (answer) => {
    if (historyPhase !== "idle") return;
    setHistoryAnswer(answer);
    setHistoryPhase("evaluating");
    setTimeout(() => {
      setHistoryPhase("revealed");
      if (answer === historyQs[historyIdx].correct) setHistoryScore(s => s + 1);
      setTimeout(() => {
        if (historyIdx < historyQs.length - 1) {
          setHistoryIdx(i => i + 1);
          setHistoryAnswer(null); setHistoryPhase("idle");
        } else { setHistoryDone(true); }
      }, 1200);
    }, 500);
  };

  // ── Allowance Handler ──
  const doAllowanceAction = async (action) => {
    const amt = parseInt(allocAmount) || 50;
    try {
      const res = await api.post("/kids-tools/allowance/action", { action, amount: amt });
      toast.success(res.data.message);
      const a = await api.get("/kids-tools/allowance");
      setAllowance(a.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // ── Budget Handler ──
  const submitBudget = async () => {
    try {
      const res = await api.post("/kids-tools/budget-challenge/submit", budgetAlloc);
      setBudgetResult(res.data);
      toast.success(`${res.data.grade} — +${res.data.xp_earned} BTC XP`);
    } catch (err) { toast.error(err.response?.data?.detail || "Budget must total 100%"); }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="kid-mode-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => tool === "menu" ? navigate("/dashboard") : setTool("menu")}
            className="text-zinc-400 hover:text-white" data-testid="kid-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">KID <span className="text-[#F7931A]">MODE</span></h1>
          <img src={MASCOT} alt="" className="w-8 h-8 rounded-lg" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* ── MENU ── */}
          {tool === "menu" && (
            <motion.div key="menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="card-dark p-4 border border-[#F7931A]/20 flex items-center gap-3">
                <img src={MASCOT} alt="Bitcoin Brain" className="w-12 h-12 rounded-xl" />
                <div>
                  <h2 className="text-white font-heading text-lg font-bold">Bitcoin Brain Jr.</h2>
                  <p className="text-zinc-400 text-sm">Learn Bitcoin through games, math, and fun! BTC XP earned on every activity.</p>
                </div>
              </div>

              <div className="bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="text-[#F7931A]" size={14} />
                  <span className="text-[#F7931A] text-xs font-medium">Earn BTC XP with every quiz</span>
                </div>
                <p className="text-zinc-500 text-xs">Complete quizzes to earn BTC XP. Keep learning to level up!</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {TOOLS.map((t, i) => (
                  <motion.button key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }} onClick={() => loadTool(t.id)}
                    className="card-dark p-4 text-center hover:border-[#F7931A]/50 transition-all"
                    data-testid={`kid-tool-${t.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] flex items-center justify-center mx-auto mb-2">
                      <t.icon className={t.color} size={20} />
                    </div>
                    <p className="text-white text-sm font-medium">{t.label}</p>
                    <p className="text-zinc-600 text-xs mt-1">{t.desc}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── SAT MATH ── */}
          {tool === "sat-math" && mathProblems.length > 0 && (
            <motion.div key="math" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-500 text-sm">Problem {mathIndex + 1} of {mathProblems.length}</span>
                <span className="badge-orange">Level {mathProblems[mathIndex].level}</span>
              </div>
              <div className="card-dark p-6 border border-[#F7931A]/20">
                <p className="text-white text-lg mb-4" data-testid="math-question">{mathProblems[mathIndex].question}</p>
                <p className="text-zinc-600 text-xs mb-3">Hint: {mathProblems[mathIndex].hint}</p>
                <div className="flex gap-2">
                  <input type="number" value={mathAnswer} onChange={e => setMathAnswer(e.target.value)}
                    placeholder="Your answer" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2 text-white font-mono focus:border-[#F7931A]/50 focus:outline-none"
                    data-testid="math-answer-input" />
                  <Button onClick={checkMath} disabled={!mathAnswer} className="bg-[#F7931A] text-black" data-testid="math-check-btn">Check</Button>
                </div>
              </div>
              {mathResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg ${mathResult.correct ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {mathResult.correct ? <CheckCircle className="text-green-400" size={18} /> : <XCircle className="text-red-400" size={18} />}
                    <span className={`font-medium ${mathResult.correct ? "text-green-400" : "text-red-400"}`}>{mathResult.message}</span>
                  </div>
                  <p className="text-[#F7931A] text-sm">+{mathResult.xp_earned} BTC XP</p>
                </motion.div>
              )}
              {mathResult && mathIndex < mathProblems.length - 1 && (
                <Button onClick={nextMath} className="w-full btn-primary" data-testid="math-next-btn">Next Problem <ChevronRight size={16} /></Button>
              )}
            </motion.div>
          )}

          {/* ── VOCABULARY ── */}
          {tool === "vocab" && vocabTerms.length > 0 && (
            <motion.div key="vocab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-500 text-sm">Card {vocabIndex + 1} of {vocabTerms.length}</span>
                <span className="badge-orange">Difficulty {vocabTerms[vocabIndex].difficulty}</span>
              </div>
              <div className="card-dark p-6 border border-purple-500/20 text-center min-h-[200px] flex flex-col justify-center cursor-pointer"
                onClick={() => setShowDef(!showDef)} data-testid="vocab-card">
                {!showDef ? (
                  <>
                    <p className="text-[#F7931A] font-mono text-xs mb-2">TAP TO REVEAL DEFINITION</p>
                    <h2 className="text-white font-heading text-3xl font-bold">{vocabTerms[vocabIndex].term}</h2>
                  </>
                ) : (
                  <>
                    <p className="text-purple-400 font-mono text-xs mb-2">{vocabTerms[vocabIndex].term}</p>
                    <p className="text-zinc-300 text-sm leading-relaxed">{vocabTerms[vocabIndex].definition}</p>
                    <p className="text-[#F7931A] text-xs mt-3">+{vocabTerms[vocabIndex].xp} BTC XP for studying</p>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setVocabIndex(Math.max(0, vocabIndex - 1)); setShowDef(false); }}
                  disabled={vocabIndex === 0} className="flex-1 btn-secondary" data-testid="vocab-prev-btn">Previous</Button>
                <Button onClick={() => { setVocabIndex(Math.min(vocabTerms.length - 1, vocabIndex + 1)); setShowDef(false); }}
                  disabled={vocabIndex === vocabTerms.length - 1} className="flex-1 btn-primary" data-testid="vocab-next-btn">Next</Button>
              </div>
            </motion.div>
          )}

          {/* ── HISTORY QUIZ ── */}
          {tool === "history" && historyQs.length > 0 && !historyDone && (
            <motion.div key="history" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-500 text-sm">Question {historyIdx + 1}/{historyQs.length}</span>
                <span className="text-[#F7931A] font-mono">{historyScore} correct</span>
              </div>
              <div className="bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-lg p-2 text-center">
                <p className="text-zinc-500 text-xs">Complete the quiz to earn BTC XP. Must finish under 2 minutes for bonus BTC XP.</p>
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={historyIdx} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <div className="card-dark p-6 border border-green-500/20">
                    <p className="text-white text-lg mb-4" data-testid="history-question">{historyQs[historyIdx].question}</p>
                    <div className="space-y-2">
                      {historyQs[historyIdx].options.map((opt, i) => {
                        const isRevealed = historyPhase === "revealed";
                        const isCorrectOpt = isRevealed && opt === historyQs[historyIdx].correct;
                        const isSelected = historyAnswer === opt;
                        const isWrongOpt = isRevealed && isSelected && opt !== historyQs[historyIdx].correct;
                        const isEval = historyPhase === "evaluating" && isSelected;
                        return (
                          <button key={i} onClick={() => handleHistoryAnswer(opt)} disabled={historyPhase !== "idle"}
                            className={`w-full text-left p-3 rounded-lg border transition-all duration-500 ${
                              isCorrectOpt ? "border-green-500 bg-green-500/20 text-green-400" :
                              isWrongOpt ? "border-red-500 bg-red-500/20 text-red-400" :
                              isEval ? "border-[#F7931A] bg-[#F7931A]/10 text-white" :
                              "border-white/10 hover:border-[#F7931A]/30 text-zinc-300"
                            }`} data-testid={`history-opt-${i}`}>
                            <span className="flex items-center gap-2">
                              {isCorrectOpt && <CheckCircle size={16} />}
                              {isWrongOpt && <XCircle size={16} />}
                              {opt}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {tool === "history" && historyDone && (
            <motion.div key="history-done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto text-center py-8 space-y-4">
              <Trophy className="text-[#F7931A] mx-auto" size={48} />
              <h2 className="text-white font-heading text-3xl font-bold">Quiz Complete!</h2>
              <p className="text-[#F7931A] font-mono text-3xl">{historyScore}/{historyQs.length}</p>
              <p className="text-zinc-400">
                {historyScore === historyQs.length ? "Perfect! You're a Bitcoin historian!" :
                 historyScore >= 3 ? "Great job! Keep learning!" : "Good try! Explore the Timeline to learn more."}
              </p>
              <p className="text-[#F7931A] text-sm">BTC XP earned for every answer — even wrong ones!</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => loadTool("history")} className="bg-[#F7931A] text-black">Play Again</Button>
                <Button onClick={() => setTool("menu")} variant="outline" className="border-white/20 text-zinc-400">Back to Menu</Button>
              </div>
            </motion.div>
          )}

          {/* ── ALLOWANCE SIMULATOR ── */}
          {tool === "allowance" && allowance && (
            <motion.div key="allowance" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto space-y-4">
              {/* Budget Setting Card */}
              <div className="card-dark p-5 border border-[#F7931A]/20">
                <h3 className="text-white font-heading font-bold mb-1 flex items-center gap-2">
                  <PiggyBank className="text-[#F7931A]" size={20} /> Sat Budget Simulator
                </h3>
                <p className="text-zinc-500 text-xs mb-4">Set your weekly sat allowance and practice smart money decisions.</p>

                {/* Editable Weekly Budget */}
                <div className="bg-[#0A0A0A] rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-zinc-400 text-xs">Weekly Budget</span>
                    {editingBudget ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={budgetInput}
                          onChange={e => setBudgetInput(e.target.value)}
                          className="w-28 bg-[#161616] border border-[#F7931A]/30 rounded px-2 py-1 text-white font-mono text-sm focus:border-[#F7931A] focus:outline-none text-right"
                          data-testid="allowance-budget-input"
                        />
                        <button
                          onClick={async () => {
                            const sats = parseInt(budgetInput);
                            if (sats >= 1) {
                              try {
                                await api.put("/kids-tools/allowance/budget", { weekly_sats: sats });
                                setAllowance(prev => ({ ...prev, weekly_sats: sats }));
                                toast.success(`Budget set to ${sats.toLocaleString()} sats`);
                                setEditingBudget(false);
                              } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                            }
                          }}
                          className="text-[#F7931A] text-xs font-medium hover:underline"
                          data-testid="allowance-budget-save"
                        >Save</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setBudgetInput(String(allowance.weekly_sats)); setEditingBudget(true); }}
                        className="text-[#F7931A] text-xs hover:underline"
                        data-testid="allowance-budget-edit"
                      >Edit</button>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[#F7931A] font-mono text-2xl font-bold">{allowance.weekly_sats?.toLocaleString()}</span>
                    <span className="text-zinc-500 text-sm">sats / week</span>
                  </div>
                  {allowance.sat_to_usd > 0 && (
                    <p className="text-zinc-500 text-xs mt-1 font-mono">
                      = ${(allowance.weekly_sats * allowance.sat_to_usd).toFixed(2)} USD
                      <span className="text-zinc-600 ml-2">(BTC ${allowance.btc_price?.toLocaleString()})</span>
                    </p>
                  )}
                </div>

                {/* Balances Grid */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    { label: "Bitcoin Savings", value: allowance.saved_btc, color: "text-[#F7931A]", icon: "sat" },
                    { label: "Cash Savings", value: allowance.saved_cash, color: "text-green-400", icon: "sat" },
                    { label: "Spent", value: allowance.spent, color: "text-red-400", icon: "sat" },
                    { label: "Donated", value: allowance.donated, color: "text-pink-400", icon: "sat" },
                  ].map((s, i) => (
                    <div key={i} className="bg-[#0A0A0A] rounded-lg p-3">
                      <p className={`font-mono font-bold text-lg ${s.color}`}>{(s.value || 0).toLocaleString()}</p>
                      <p className="text-zinc-600 text-xs">{s.label}</p>
                      {allowance.sat_to_usd > 0 && (
                        <p className="text-zinc-700 text-xs font-mono">${((s.value || 0) * allowance.sat_to_usd).toFixed(2)}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Action Input */}
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <input type="number" value={allocAmount} onChange={e => setAllocAmount(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-[#F7931A]/50 focus:outline-none pr-12"
                      placeholder="Amount"
                      data-testid="allowance-amount" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">sats</span>
                  </div>
                </div>
                {allocAmount && allowance.sat_to_usd > 0 && (
                  <p className="text-zinc-500 text-xs mb-3 font-mono text-center">
                    {parseInt(allocAmount || 0).toLocaleString()} sats = ${(parseInt(allocAmount || 0) * allowance.sat_to_usd).toFixed(2)} USD
                  </p>
                )}

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { action: "save_btc", label: "Save to Bitcoin", color: "bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30" },
                    { action: "save_cash", label: "Save Cash", color: "bg-green-500/20 text-green-400 border-green-500/30" },
                    { action: "spend", label: "Spend", color: "bg-red-500/20 text-red-400 border-red-500/30" },
                    { action: "donate", label: "Donate", color: "bg-pink-500/20 text-pink-400 border-pink-500/30" },
                  ].map(a => (
                    <button key={a.action} onClick={() => doAllowanceAction(a.action)}
                      className={`p-2.5 rounded-lg border text-sm font-medium ${a.color}`}
                      data-testid={`allowance-${a.action}`}>{a.label}</button>
                  ))}
                </div>
                <p className="text-[#F7931A] text-xs mt-3 text-center">Saving in Bitcoin earns the most BTC XP!</p>
              </div>

              {/* History */}
              {allowance.history && allowance.history.length > 0 && (
                <div className="card-dark p-4">
                  <h4 className="text-zinc-400 text-xs font-medium mb-2">Recent Activity</h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {[...allowance.history].reverse().slice(0, 10).map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                        <span className={`font-medium ${
                          h.action === "save_btc" ? "text-[#F7931A]" :
                          h.action === "donate" ? "text-pink-400" :
                          h.action === "spend" ? "text-red-400" : "text-green-400"
                        }`}>{h.action.replace("_", " ")}</span>
                        <span className="text-zinc-400 font-mono">{(h.amount || 0).toLocaleString()} sats</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ── BUDGET CHALLENGE ── */}
          {tool === "budget" && budget && (
            <motion.div key="budget" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto space-y-4">
              <div className="card-dark p-5 border border-cyan-500/20">
                <h3 className="text-white font-heading font-bold mb-1 flex items-center gap-2">
                  <Wallet className="text-cyan-400" size={20} /> Family Budget Challenge
                </h3>
                <p className="text-zinc-500 text-xs mb-4">{budget.rules}</p>
                <p className="text-zinc-400 text-xs mb-3">Total must equal 100%. Remaining: <span className={`font-mono ${100 - Object.values(budgetAlloc).reduce((a, b) => a + b, 0) === 0 ? "text-green-400" : "text-red-400"}`}>
                  {100 - Object.values(budgetAlloc).reduce((a, b) => a + b, 0)}%
                </span></p>

                <div className="space-y-3">
                  {budget.categories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-3">
                      <span className="text-zinc-400 text-xs w-28 flex-shrink-0">{cat.name}</span>
                      <input type="range" min={cat.min_pct} max={50} value={budgetAlloc[cat.id] || 0}
                        onChange={e => setBudgetAlloc(prev => ({ ...prev, [cat.id]: parseInt(e.target.value) }))}
                        className="flex-1 accent-[#F7931A]" data-testid={`budget-${cat.id}`} />
                      <span className={`font-mono text-xs w-8 text-right ${cat.id === "savings_btc" ? "text-[#F7931A]" : "text-zinc-400"}`}>
                        {budgetAlloc[cat.id] || 0}%
                      </span>
                    </div>
                  ))}
                </div>

                <Button onClick={submitBudget} className="w-full mt-4 bg-[#F7931A] text-black font-bold" data-testid="budget-submit-btn">
                  Submit Budget
                </Button>
              </div>

              {budgetResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="card-dark p-4 border border-[#F7931A]/30">
                  <p className="text-white font-bold">{budgetResult.grade}</p>
                  <p className="text-[#F7931A] text-sm">+{budgetResult.xp_earned} BTC XP</p>
                  <p className="text-zinc-400 text-xs mt-2 italic">{budgetResult.tip}</p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── LESSONS ── */}
          {tool === "lessons" && !sectionDetail && (
            <motion.div key="lessons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {user?.purchase_tier === "free" && (
                <div className="bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl p-4 flex items-center gap-3" data-testid="kid-upgrade-banner">
                  <Lock className="text-[#F7931A] flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Free Preview — Section 1 available</p>
                    <p className="text-zinc-500 text-xs">Upgrade to unlock all 21 kid-friendly sections</p>
                  </div>
                  <Button size="sm" className="btn-primary text-xs h-7" onClick={() => navigate("/subscription")} data-testid="kid-upgrade-btn">
                    Upgrade
                  </Button>
                </div>
              )}
              {kidSections.map((s, i) => {
                const isLocked = user?.purchase_tier === "free" && i > 0;
                return (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                    className={`card-dark p-4 flex items-center gap-3 transition-all ${isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-[#F7931A]/50"}`}
                    onClick={() => !isLocked && loadKidSection(s.id)} data-testid={`kid-section-${i}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0">
                      {isLocked ? <Lock className="text-zinc-600" size={14} /> :
                       s.completed ? <CheckCircle className="text-green-400" size={14} /> :
                       <span className="text-[#F7931A] font-mono text-sm font-bold">{i + 1}</span>}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isLocked ? "text-zinc-600" : "text-white"}`}>{s.title}</p>
                      <p className="text-zinc-600 text-xs">{s.difficulty}</p>
                    </div>
                    {isLocked ? <Lock className="text-zinc-700" size={16} /> :
                     s.completed ? <CheckCircle className="text-green-500" size={16} /> :
                     <ChevronRight className="text-zinc-700" size={16} />}
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* ── SECTION DETAIL VIEW ── */}
          {tool === "lessons" && sectionDetail && (
            <motion.div key="section-detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <button onClick={backToSectionList} className="flex items-center gap-1 text-zinc-400 hover:text-white text-sm" data-testid="kid-back-btn">
                <ChevronRight className="rotate-180" size={16} /> Back to Lessons
              </button>

              <div className="card-dark p-5">
                <h3 className="font-heading text-xl font-bold text-white mb-1" data-testid="kid-section-title">{sectionDetail.title}</h3>
                <span className="text-xs text-[#F7931A] bg-[#F7931A]/10 px-2 py-0.5 rounded">{sectionDetail.difficulty}</span>
              </div>

              {/* Story */}
              <div className="card-dark p-5" data-testid="kid-section-story">
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{sectionDetail.story}</p>
              </div>

              {/* Quiz */}
              {!kidQuizResult && (
                <div className="card-dark p-5 space-y-4" data-testid="kid-quiz">
                  <h4 className="font-heading text-lg font-bold text-white">Quiz Time!</h4>
                  {sectionDetail.quiz.map((q, qi) => (
                    <div key={qi} className="space-y-2">
                      <p className="text-white text-sm font-medium">{qi + 1}. {q.question}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {q.options.map((opt, oi) => (
                          <button key={oi}
                            onClick={() => setKidQuizAnswers(prev => ({ ...prev, [qi]: opt }))}
                            className={`text-left p-3 rounded-lg border text-sm transition-all ${
                              kidQuizAnswers[qi] === opt
                                ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                                : "bg-[#161616] border-white/10 text-zinc-300 hover:border-white/20"
                            }`}
                            data-testid={`kid-quiz-q${qi}-opt${oi}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <Button
                    onClick={submitKidQuiz}
                    disabled={kidQuizSubmitting || Object.keys(kidQuizAnswers).length < sectionDetail.quiz.length}
                    className="btn-primary w-full"
                    data-testid="kid-submit-quiz-btn"
                  >
                    {kidQuizSubmitting ? "Checking..." : "Submit Answers"}
                  </Button>
                </div>
              )}

              {/* Quiz Result */}
              {kidQuizResult && (
                <div className={`card-dark p-5 text-center ${kidQuizResult.passed ? "border-green-500/30" : "border-red-500/30"}`} data-testid="kid-quiz-result">
                  {kidQuizResult.passed ? (
                    <>
                      <CheckCircle className="text-green-400 mx-auto mb-2" size={40} />
                      <h4 className="font-heading text-xl font-bold text-white mb-1">Great Job!</h4>
                      <p className="text-green-400 font-mono text-lg">+{kidQuizResult.xp_earned} BTC XP</p>
                      <p className="text-zinc-500 text-sm mt-1">{kidQuizResult.correct}/{kidQuizResult.total} correct</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-red-400 mx-auto mb-2" size={40} />
                      <h4 className="font-heading text-xl font-bold text-white mb-1">Almost!</h4>
                      <p className="text-zinc-400 text-sm">{kidQuizResult.correct}/{kidQuizResult.total} correct — read the story again and try once more!</p>
                    </>
                  )}
                  <div className="flex gap-3 mt-4 justify-center">
                    {!kidQuizResult.passed && (
                      <Button onClick={() => { setKidQuizAnswers({}); setKidQuizResult(null); }} className="btn-primary" data-testid="kid-retry-btn">
                        Try Again
                      </Button>
                    )}
                    <Button onClick={backToSectionList} variant="ghost" className="text-zinc-400" data-testid="kid-back-to-lessons-btn">
                      Back to Lessons
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
